import { AxiosResponse } from "axios";

import { APIHandler, UnauthedAPIHandler } from "../../base";
import { PresignedUrlsRequest } from "../../../common/interfaces/admin/presigned-urls-request.interface";
import { LearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import { CitationDisplay } from "../../../common/enums/citation-display.enum";
import {
    ProviderRequest,
    RefreshProviderRequest
} from "../../../common/interfaces/admin/provider-request.interface";
import {
    CrawlConfig,
    CrawlItemPreview,
    CrawlItemsPage,
    CrawlItemsQuery,
    CrawlRun,
    CrawlSchedule,
    ReviewPolicy
} from "../../../common/interfaces/admin/crawl.interface";

export const fetchProviders = (companyId: number) =>
    APIHandler.get(`/admin/${companyId}/contentItemProviders`);

export const fetchProvidersByKB = (companyId: number, kbUid: string) =>
    APIHandler.get(`/admin/${companyId}/knowledgeBases/${kbUid}/contentItemProviders`);

export const fetchContentFolders = (companyId: number, providerId: number) =>
    APIHandler.post(`/admin/${companyId}/contentItemProviders/folders`, {
        contentItemProviderId: providerId
    });

export const fetchLearningObjects = (companyId: number, courseId: string, query?: string) =>
    APIHandler.get(`/admin/${companyId}/course/${courseId}/learningObjects${query ?? ""}`);

export const deleteLearningObjects = (companyId: number, courseId: string, loItemIds: string[]) =>
    APIHandler.delete(`/admin/${companyId}/course/${courseId}/learningObjects`, {
        data: { loItemIds }
    });

export const fetchLearningObject = (
    companyId: number,
    courseId: string,
    learningObjectId: string
): Promise<AxiosResponse<LearningObject, any>> =>
    APIHandler.get(`/admin/${companyId}/course/${courseId}/learningObjects/${learningObjectId}`);

export const getLearningObjectDownloadUrl = (
    companyId: number,
    courseId: string,
    learningObjectId: string
): Promise<AxiosResponse<{ url: string }, any>> =>
    APIHandler.post(
        `/admin/${companyId}/course/${courseId}/learningObjects/${learningObjectId}/download`
    );

export interface SetVisibilityRequest {
    loItemIds: string[];
    // undefined = leave unchanged; null = inherit provider default; value = override.
    citationDisplay?: CitationDisplay | null;
    managerDownload?: boolean | null;
}

export const setLearningObjectsVisibility = (
    companyId: number,
    courseId: string,
    data: SetVisibilityRequest
): Promise<AxiosResponse<LearningObject[], any>> =>
    APIHandler.patch(`/admin/${companyId}/course/${courseId}/learningObjects/visibility`, data);

export const fetchPresignedUrls = (
    companyId: number,
    providerId: number,
    data: PresignedUrlsRequest
) => APIHandler.post(`/admin/${companyId}/contentItemProviders/${providerId}/files`, data);

export const uploadFile = (url: string, file: File, options?: object) =>
    UnauthedAPIHandler.put(url, file, options);

export const confirmFile = (companyId: number, providerId: number, fileId: string) =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${providerId}/files/${fileId}/confirm`
    );

export const cancelFile = (companyId: number, providerId: number, fileId: string) =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${providerId}/files/${fileId}/cancel`
    );

export const addContentItemProvider = (companyId: number, data: ProviderRequest) =>
    APIHandler.post(`/admin/${companyId}/contentItemProviders`, data);

export const deleteContentItemProvider = (companyId: number, contentItemProviderId: number) =>
    APIHandler.delete(`/admin/${companyId}/contentItemProviders/${contentItemProviderId}`);

export const isSharedContentItemProvider = (companyId: number, contentItemProviderId: number) =>
    APIHandler.get(`/admin/${companyId}/contentItemProviders/${contentItemProviderId}/isPublic`);

export const checkNeedsAccessToken = (companyId: number, contentItemProviderId: number) =>
    APIHandler.get<{ needsAccessToken: boolean }>(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/needsAccessToken`
    );

export const refreshContentItemProvider = (
    companyId: number,
    contentItemProviderId: number,
    data: RefreshProviderRequest
) =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/refresh`,
        data
    );

export interface SetProviderVisibilityRequest {
    defaultCitationDisplay?: CitationDisplay;
    defaultManagerDownload?: boolean;
    // Opt-in live Google Sheets deep links on citations (Google Drive providers).
    citationLiveLink?: boolean;
}

export const setProviderVisibilityDefault = (
    companyId: number,
    contentItemProviderId: number,
    data: SetProviderVisibilityRequest
) =>
    APIHandler.patch(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/visibility`,
        data
    );

export const resetProviderVisibility = (
    companyId: number,
    contentItemProviderId: number,
    data: SetProviderVisibilityRequest
): Promise<AxiosResponse<{ updatedFiles: number }, any>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/visibility/reset`,
        data
    );

// ---------------------------------------------------------------------------
// Web scraper: crawl configuration and the review gate
// (web_scraper_provider_design.md §8.5, D16).
// ---------------------------------------------------------------------------

export interface CrawlConfigUpdateRequest {
    crawlConfig?: Partial<CrawlConfig>;
    reviewPolicy?: ReviewPolicy;
    crawlSchedule?: CrawlSchedule;
    robotsOverride?: boolean;
}

export const updateCrawlConfig = (
    companyId: number,
    contentItemProviderId: number,
    data: CrawlConfigUpdateRequest
) =>
    APIHandler.patch(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-config`,
        data
    );

export const fetchCrawlRuns = (
    companyId: number,
    contentItemProviderId: number
): Promise<AxiosResponse<CrawlRun[], any>> =>
    APIHandler.get(`/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs`);

export const fetchCrawlRun = (
    companyId: number,
    contentItemProviderId: number,
    runId: string
): Promise<AxiosResponse<CrawlRun, any>> =>
    APIHandler.get(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}`
    );

export const fetchCrawlItems = (
    companyId: number,
    contentItemProviderId: number,
    runId: string,
    query: CrawlItemsQuery = {}
): Promise<AxiosResponse<CrawlItemsPage, any>> => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
    });
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return APIHandler.get(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/items${suffix}`
    );
};

export const fetchCrawlItemPreview = (
    companyId: number,
    contentItemProviderId: number,
    runId: string,
    itemId: string,
    variant: "snapshot" | "raw" = "snapshot"
): Promise<AxiosResponse<CrawlItemPreview, any>> =>
    APIHandler.get(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/items/${itemId}/preview?variant=${variant}`
    );

export const setCrawlDecisions = (
    companyId: number,
    contentItemProviderId: number,
    runId: string,
    itemIds: string[],
    decision: "include" | "exclude"
): Promise<AxiosResponse<{ updated: number }, any>> =>
    APIHandler.patch(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/items`,
        { itemIds, decision }
    );

export const addCrawlExclusion = (
    companyId: number,
    contentItemProviderId: number,
    runId: string,
    pattern: string,
    persist: boolean
): Promise<AxiosResponse<{ matched: number; persisted: boolean }, any>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/exclusions`,
        { pattern, persist }
    );

export const approveCrawlRun = (
    companyId: number,
    contentItemProviderId: number,
    runId: string
): Promise<AxiosResponse<CrawlRun, any>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/approve`
    );

export const refetchPage = (
    companyId: number,
    contentItemProviderId: number,
    loItemId: string
): Promise<AxiosResponse<{ url: string }>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/pages/${loItemId}/refetch`
    );

export const excludePage = (
    companyId: number,
    contentItemProviderId: number,
    loItemId: string
): Promise<AxiosResponse<{ url: string; pattern: string }>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/pages/${loItemId}/exclude`
    );

export const downloadCrawlItemsCsv = (
    companyId: number,
    contentItemProviderId: number,
    runId: string
): Promise<AxiosResponse<Blob>> =>
    APIHandler.get(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/items/export`,
        { responseType: "blob" }
    );

export const discardCrawlRun = (
    companyId: number,
    contentItemProviderId: number,
    runId: string
): Promise<AxiosResponse<CrawlRun, any>> =>
    APIHandler.post(
        `/admin/${companyId}/contentItemProviders/${contentItemProviderId}/crawl-runs/${runId}/discard`
    );
